import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faPlus, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CommunicationService, sharedApiClient } from '../../fetcher';
import { useAuth, UserAbility, useNotifications } from '@medprompt/shared';
import TemplateEditorModal from '../TemplateEditorModal';
import '@medprompt/shared/src/common/modals.css';
import './TemplateManager.css';

const CommTemplateManagerModal = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const hasDocsWrite = Boolean(user?.authorities?.includes(UserAbility.DOCS_WRITE.key));
  const docsWriteTitle = 'You do not have permission to edit documents';
  
  const communicationService = useMemo(() => new CommunicationService(sharedApiClient), []);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedTemplates = await communicationService.fetchCommTemplates();
      setTemplates(fetchedTemplates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
      addNotification('template-manager', 'Failed to load templates', { isError: true });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = () => {
    if (!hasDocsWrite) {
      return;
    }
    setEditingTemplate(null);
    setShowEditorModal(true);
  };

  const handleEditTemplate = (template) => {
    if (!hasDocsWrite) {
      return;
    }
    setEditingTemplate(template);
    setShowEditorModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!hasDocsWrite) {
      return;
    }
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTemplateId(templateId);
      await communicationService.deleteCommTemplate(templateId);
      
      addNotification('template-manager', 'Template deleted successfully', { isError: false });
      
      // Refresh the list
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      addNotification('template-manager', 'Failed to delete template', { isError: true });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleTemplateSave = async (templateData) => {
    if (!hasDocsWrite) {
      return;
    }
    try {
      if (editingTemplate) {
        // Update existing template
        const updateData = {
          templateId: editingTemplate.templateId,
          title: templateData.title,
          content: templateData.content
        };
        await communicationService.updateCommTemplate(updateData);
        
        addNotification('template-manager', 'Template updated successfully', { isError: false });
      } else {
        // Create new template
        const createData = {
          templateId: null,
          title: templateData.title,
          content: templateData.content
        };
        await communicationService.createCommTemplate(createData);
        
        addNotification('template-manager', 'Template created successfully', { isError: false });
      }
      
      // Close editor modal and refresh list
      setShowEditorModal(false);
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      addNotification('template-manager', `Failed to ${editingTemplate ? 'update' : 'create'} template`, { isError: true });
      throw err; // Re-throw to let TemplateEditorModal handle it
    }
  };

  const handleCloseEditor = () => {
    setShowEditorModal(false);
    setEditingTemplate(null);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container template-manager-modal modal-animated" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header template-manager-header">
            <h2>Manage Communication Templates</h2>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="modal-content template-manager-content">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#50b97d' }} />
                <p style={{ marginTop: '15px', color: '#666' }}>Loading templates...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ color: '#dc3545', marginBottom: '15px' }}>{error}</p>
                <button className="button" onClick={fetchTemplates}>
                  Retry
                </button>
              </div>
            ) : (
              <>
                {/* Template List */}
                <div className="template-list">
                  {templates.length === 0 ? (
                    <div className="empty-templates-message">
                      No templates created yet. Click "Add Template" to create one.
                    </div>
                  ) : (
                    templates.map((template) => (
                      <div key={template.templateId} className="template-item">
                        <div className="template-info">
                          <div className="template-title">{template.title}</div>
                          <div className="template-content-preview">
                            {template.content.length > 150 
                              ? `${template.content.substring(0, 150)}...` 
                              : template.content}
                          </div>
                        </div>
                        <div className="template-actions">
                          <button
                            className="other-button edit-button"
                            onClick={() => handleEditTemplate(template)}
                            disabled={!hasDocsWrite || deletingTemplateId === template.templateId}
                            title={!hasDocsWrite ? docsWriteTitle : 'Edit template'}
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button
                            className="other-button delete-template-button"
                            onClick={() => handleDeleteTemplate(template.templateId)}
                            disabled={!hasDocsWrite || deletingTemplateId === template.templateId}
                            title={!hasDocsWrite ? docsWriteTitle : 'Delete template'}
                          >
                            {deletingTemplateId === template.templateId ? (
                              <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} />
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Template Button */}
                <button
                  className="add-template-button"
                  onClick={handleAddTemplate}
                  disabled={!hasDocsWrite || loading}
                  title={!hasDocsWrite ? docsWriteTitle : undefined}
                >
                  <FontAwesomeIcon icon={faPlus} className="add-icon" />
                  Add Template
                </button>
              </>
            )}
          </div>

          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Template Editor Modal */}
      {showEditorModal && (
        <TemplateEditorModal
          template={editingTemplate}
          onSave={handleTemplateSave}
          onClose={handleCloseEditor}
        />
      )}
    </>
  );
};

export default React.memo(CommTemplateManagerModal);
